import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { usePermissionCheck } from '../../contexts/PermissionContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import {
  HiOutlineFolder,
  HiOutlineArrowLeft,
  HiOutlineTrash,
  HiOutlineDocumentText,
} from 'react-icons/hi2'
import DocumentTable from '../../components/documents/DocumentTable'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import {
  Badge,
  Button,
  Card,
  IconButton,
  Spinner,
} from '../../components/ui'

export default function GroupDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { canDelete } = usePermissionCheck()
  const { getEntity } = useWorkspace()

  const [group, setGroup] = useState(null)
  const [loading, setLoading] = useState(true)
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', onConfirm: null, type: 'warning',
  })

  const permissions = {
    canDeleteGroup: canDelete('document_group'),
    isOwner: !isWorkspaceSwitched,
  }

  useEffect(() => {
    fetchGroup()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isWorkspaceSwitched])

  async function fetchGroup() {
    try {
      setLoading(true)
      const params = {}
      if (isWorkspaceSwitched && ownerAccountId) params.owner_account_id = ownerAccountId
      const response = await apiService.get(`/document-groups/${id}/`, { params })
      if (response.success) {
        setGroup(response.data.data || response.data)
      } else {
        showError(t('cases.loadFailed', { entity: `${getEntity('document').singular.toLowerCase()} group` }))
        navigate('/document-groups')
      }
    } catch {
      showError(t('cases.loadFailed', { entity: `${getEntity('document').singular.toLowerCase()} group` }))
      navigate('/document-groups')
    } finally {
      setLoading(false)
    }
  }

  async function handleDeleteGroup() {
    try {
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.delete(`/document-groups/${id}/`, { params })
      if (response.success) {
        showSuccess(t('documents.deleteSuccess', { entity: `${getEntity('document').singular} group` }))
        navigate('/document-groups')
      } else {
        showError(response.error?.message || t('documents.deletedFailed', { entity: `${getEntity('document').singular.toLowerCase()} group` }))
      }
    } catch {
      showError(t('documents.deletedFailed', { entity: `${getEntity('document').singular.toLowerCase()} group` }))
    }
  }

  const confirmDeleteGroup = () => {
    const docCount = group?.document_count || 0
    setConfirmDialog({
      isOpen: true,
      title: t('documents.groups.deleteTitle', { name: group?.name }),
      message: docCount > 0
        ? t('documents.groups.deleteWithDocs', {
            count: docCount,
            entity: docCount === 1 ? getEntity('document').singular.toLowerCase() : getEntity('document').plural.toLowerCase(),
          })
        : t('documents.groups.deleteEmpty'),
      type: 'danger',
      confirmText: t('documents.groups.deleteConfirm'),
      onConfirm: () => {
        handleDeleteGroup()
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
      },
    })
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-muted rounded-lg animate-pulse" />
          <div className="h-7 bg-muted rounded w-1/3 animate-pulse" />
        </div>
        <Card padding="md"><div className="h-64 bg-muted rounded-lg animate-pulse" /></Card>
        <div className="flex justify-center"><Spinner /></div>
      </div>
    )
  }

  if (!group) return null

  const docCount = group.document_count || 0

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <IconButton
            icon={HiOutlineArrowLeft}
            aria-label={t('cases.detail.backTo', { entity: t('documents.groups.title', { entity: getEntity('document').singular }) })}
            variant="ghost"
            onClick={() => navigate('/document-groups')}
            className="rtl:rotate-180"
          />
          <span className="w-10 h-10 bg-info-soft text-info rounded-xl flex items-center justify-center flex-shrink-0">
            <HiOutlineFolder className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-ink-primary truncate">{group.name}</h1>
            <p className="text-sm text-ink-muted truncate">{group.description || '—'}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Badge tone="info" size="md">
            <HiOutlineDocumentText className="w-3.5 h-3.5" />
            {docCount} {docCount === 1 ? getEntity('document').singular.toLowerCase() : getEntity('document').plural.toLowerCase()}
          </Badge>
          {permissions.isOwner && permissions.canDeleteGroup && (
            <Button
              variant="secondary"
              leadingIcon={HiOutlineTrash}
              onClick={confirmDeleteGroup}
              className="!text-danger hover:!bg-danger-soft hover:!border-danger/40"
            >
              {t('documents.groups.deleteConfirm')}
            </Button>
          )}
        </div>
      </div>

      <DocumentTable
        context="group"
        contextId={parseInt(id)}
        contextName={group.name}
        onDocumentUpdate={fetchGroup}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  )
}
